import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import {
  InvoiceInvalidError,
  InvoiceRefusedError,
  useInvoiceLink,
  type Invoice,
} from '@/routes/invoices/useInvoices';

/**
 * The office's handle on the customer's invoice link (M2-16, pass B) — M2-07's
 * `CustomerLink`, for invoices. Copy puts the full `/i/{token}` URL on the
 * clipboard: the live link, or a new one after a revoke (`POST …/link`).
 * Revoke, after a confirm, kills the link in every email already sent
 * (`DELETE …/link`) — and pauses reminders, which compose nothing without a
 * live link, until a copy or a resend issues a new one; the confirm says so.
 *
 * Rendered by `InvoiceActs` only for `customers.manage` and only for an
 * invoice the API hands a link out for (sent, not void). The server still
 * decides; a refused copy shows its own sentence.
 *
 * The URL is also shown in a read-only box once copied, so it can be copied
 * by hand where the clipboard is not available.
 */
export function InvoiceCustomerLink({ invoice }: { invoice: Invoice }) {
  const { t } = useTranslation();
  const id = useId();
  const { copy, revoke } = useInvoiceLink(invoice.id);
  const [url, setUrl] = useState<string | null>(null);
  const [clipboardFailed, setClipboardFailed] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);

  const pending = copy.isPending || revoke.isPending;

  function explain(error: unknown) {
    setProblem(
      error instanceof InvoiceRefusedError || error instanceof InvoiceInvalidError
        ? t('invoices.acts.refused', { reason: error.message })
        : t('invoices.acts.failed'),
    );
  }

  function onCopy() {
    setProblem(null);
    setAnnouncement('');
    copy.mutate(undefined, {
      onSuccess: async (link) => {
        setUrl(link);
        try {
          await navigator.clipboard.writeText(link);
          setClipboardFailed(false);
          setAnnouncement(t('invoices.link.copied'));
        } catch {
          // Announced as well as shown (M2-07's review): a screen-reader user
          // otherwise cannot tell a copy that failed from one that worked.
          setClipboardFailed(true);
          setAnnouncement(t('invoices.link.copyManually'));
        }
      },
      onError: explain,
    });
  }

  function onRevoke() {
    setProblem(null);
    revoke.mutate(undefined, {
      onSuccess: () => {
        setUrl(null);
        setClipboardFailed(false);
        setConfirmingRevoke(false);
        setAnnouncement(t('invoices.link.revoked'));
      },
      onError: explain,
    });
  }

  return (
    <div
      role="group"
      aria-labelledby={`${id}-heading`}
      className="space-y-3 border-t border-[var(--color-border)] pt-4"
    >
      <h3 id={`${id}-heading`} className="text-sm font-semibold">
        {t('invoices.link.heading')}
      </h3>
      <p className="text-sm text-[var(--color-muted-foreground)]">{t('invoices.link.help')}</p>

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" disabled={pending} onClick={onCopy}>
          {copy.isPending ? t('invoices.link.copying') : t('invoices.link.copy')}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={pending}
          onClick={() => {
            setProblem(null);
            revoke.reset();
            setConfirmingRevoke(true);
          }}
        >
          {t('invoices.link.revoke')}
        </Button>
      </div>

      {url ? (
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('invoices.link.url')}</span>
          <input
            readOnly
            className={FIELD_CLASS}
            value={url}
            onFocus={(event) => event.currentTarget.select()}
          />
        </label>
      ) : null}
      {clipboardFailed ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {t('invoices.link.copyManually')}
        </p>
      ) : null}

      {problem && !confirmingRevoke ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {problem}
        </p>
      ) : null}

      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>

      {confirmingRevoke ? (
        <ConfirmDialog
          titleId={`${id}-revoke-title`}
          title={t('invoices.link.revokeTitle')}
          description={t('invoices.link.revokeDescription')}
          confirmLabel={t('invoices.link.revokeConfirm')}
          destructive
          isPending={revoke.isPending}
          errorMessage={revoke.isError ? (problem ?? undefined) : undefined}
          onConfirm={onRevoke}
          onClose={() => setConfirmingRevoke(false)}
        />
      ) : null}
    </div>
  );
}
